import { AirportLocation, AirportLocations } from '@reward-platform/flights-schemas'
import {
  AncillariesLocationSchema,
  AncillariesLocation,
  CarHireLocationSchema,
  CarHireLocation,
  LocationSearchParams,
} from '@reward-platform/ancillaries-locations'
import { GetAirportsParamsSchema } from '@reward-platform/ancillaries-schemas/location'
import { PlatformError } from '~/utils/errors'
import { nextApiClient } from './clients/nextApiClient'
import { createServiceSchemaValidator } from './utils'

const validateAncillariesLocation = createServiceSchemaValidator(AncillariesLocationSchema.array())
const validateCarHireLocation = createServiceSchemaValidator(CarHireLocationSchema.array())

async function getLocations(
  searchType: 'HOTEL' | 'EXPERIENCE' | 'CAR_HIRE',
  { searchString, latitude, longitude, locale, vendorBrandId, multiBrand }: LocationSearchParams
): Promise<AncillariesLocation[] | CarHireLocation[]> {
  if (!searchType || (!searchString?.trim() && latitude == null && longitude == null)) {
    throw new PlatformError('Missing location search parameters')
  }

  const searchParams = new URLSearchParams()

  if (latitude && longitude) {
    searchParams.set('latitude', latitude.toString())
    searchParams.set('longitude', longitude.toString())
  } else if (searchString) {
    searchParams.set('searchString', searchString)
  }

  searchParams.set('locale', locale)

  if (vendorBrandId) {
    searchParams.set('vendorBrandId', vendorBrandId)
  }
  if (multiBrand) {
    searchParams.set('multiBrand', 'true')
  }
  let url = '/search/locations'
  switch (searchType) {
    case 'HOTEL':
      url += `/hotel`
      break
    case 'EXPERIENCE':
      url += `/experience`
      break
    case 'CAR_HIRE':
      url += `/car-hire`
      break
    default:
      break
  }
  url += `?${searchParams.toString()}`

  const { data } = await nextApiClient.get(url)

  return data
}

export async function getHotelLocations(
  params: LocationSearchParams
): Promise<AncillariesLocation[]> {
  const response = await getLocations('HOTEL', params)
  return validateAncillariesLocation(response)
}

export async function getCarLocations(params: LocationSearchParams): Promise<CarHireLocation[]> {
  const response = await getLocations('CAR_HIRE', params)
  return validateCarHireLocation(response)
}

export async function getExperienceLocations(
  params: LocationSearchParams
): Promise<AncillariesLocation[]> {
  const response = await getLocations('EXPERIENCE', params)
  return validateAncillariesLocation(response)
}

interface AirportLocationsQuery {
  partnerId: string
  searchString?: string
  fromAirport?: string
}

export async function getAirportLocations({
  partnerId,
  fromAirport,
  searchString,
}: AirportLocationsQuery): Promise<AirportLocations> {
  const validatedParams = GetAirportsParamsSchema.parse({
    searchString: searchString?.trim().toLowerCase(),
    partnerIds: partnerId,
    fromAirport,
  })

  const searchParams = new URLSearchParams()
  searchParams.set('partnerIds', validatedParams.partnerIds)

  if (validatedParams.searchString?.length) {
    searchParams.set('searchString', validatedParams.searchString)
  }

  if (validatedParams.fromAirport) {
    searchParams.set('fromAirport', validatedParams.fromAirport)
  }

  const { data } = await nextApiClient.get(`/search/locations/airports?${searchParams.toString()}`)
  return data
}

export const isValidRoute = async (
  fromAirportCode: string,
  partnerId: string,
  toAirportCode: string
): Promise<boolean> => {
  const locations = await getAirportLocations({
    searchString: toAirportCode,
    partnerId,
    fromAirport: fromAirportCode,
  })

  return locations.some((l: AirportLocation) => l.airportCode === toAirportCode.toUpperCase())
}

/**
 * @param partnerIds   Comma separated list of Partner Ids
 * @param airportCodes Comma separated list of Airports
 */
export const getAirports = async (
  partnerIds: string,
  airportCodes: string
): Promise<AirportLocations | null> => {
  const { data } = await nextApiClient.get<AirportLocations>(
    `/search/airports?partnerIds=${partnerIds}&airportCodes=${airportCodes}`
  )
  return data
}
